import { Link } from 'react-router-dom';
import { useKnowledgeBases } from '../context/KnowledgeBaseContext';

interface Props {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function KnowledgeBasePicker({ selectedIds, onSelectionChange }: Props) {
  const { knowledgeBases } = useKnowledgeBases();

  const readyKBs = knowledgeBases.filter(kb => kb.status === 'ready');

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(i => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-3">
      {readyKBs.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-4 font-medium">
          No Ready Knowledge Base Found
        </div>
      ) : (
        readyKBs.map(kb => {
          const isSelected = selectedIds.includes(kb.id);
          return (
            <button
              key={kb.id}
              type="button"
              onClick={() => toggle(kb.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-btn border transition-colors text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              {/* Checkbox */}
              <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
                isSelected
                  ? 'border-primary-600 bg-primary-600'
                  : 'border-neutral-300 bg-white'
              }`}>
                {isSelected && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {/* KB info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-800 truncate">{kb.name}</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {kb.file_count} {kb.file_count === 1 ? 'file' : 'files'}
                </div>
              </div>
            </button>
          );
        })
      )}

      {/* Hint */}
      <p className="text-xs text-neutral-400 mt-2">
        You can skip this step — assistants work without knowledge bases.
      </p>

      {/* CTA to create KB */}
      <div>
        <Link
          to="/upload"
          className="inline-flex items-center justify-center rounded-btn border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-all hover:bg-neutral-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Create New Knowledge Base
        </Link>
      </div>
    </div>
  );
}
